import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Maximize, Eye, Camera } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import { fmtINR } from "@/lib/site";

export default function RoomCard({ room, index = 0 }) {
  const [lightbox, setLightbox] = useState(null);
  const galleryImages = (room.gallery || [room.image]).map((src, i) => ({
    src,
    alt: `${room.name} photo ${i + 1} - Sagar Darshan Guest House`,
  }));
  return (
    <motion.article
      data-testid={`room-card-${room.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col border border-navy/10 bg-white"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <Link to={`/rooms/${room.id}`} className="block h-full w-full">
          <img
            src={room.image}
            alt={`${room.name} at Sagar Darshan Guest House, Somnath`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </Link>
        <span className="absolute left-4 top-4 bg-navy/85 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur-sm">
          {room.view}
        </span>
        <button
          data-testid={`room-${room.id}-gallery-btn`}
          onClick={() => setLightbox(0)}
          className="absolute bottom-4 right-4 flex items-center gap-2 bg-navy/85 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-sand backdrop-blur-sm transition-colors duration-300 hover:bg-gold hover:text-navy"
        >
          <Camera size={13} /> See Gallery
        </button>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <h3 className="font-display text-2xl text-navy">{room.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-navy/60">{room.short}</p>
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-navy/70">
          <span className="flex items-center gap-1.5"><BedDouble size={13} strokeWidth={1.5} className="text-gold-dark" /> {room.bed}</span>
          <span className="flex items-center gap-1.5"><Maximize size={13} strokeWidth={1.5} className="text-gold-dark" /> {room.size}</span>
          <span className="flex items-center gap-1.5"><Eye size={13} strokeWidth={1.5} className="text-gold-dark" /> {room.view}</span>
        </div>
        <div className="mt-6 flex items-end justify-between border-t border-navy/10 pt-5">
          <div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-navy/50">Starting from</span>
            <p className="font-display text-xl text-navy">{fmtINR(room.price)} <span className="font-body text-xs text-navy/50">/ night</span></p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Link to={`/rooms/${room.id}`} data-testid={`room-${room.id}-details-btn`} className="btn-outline-dark !px-4 !py-3 text-center">
            View Details
          </Link>
          <Link to={`/book?room=${room.id}`} data-testid={`room-${room.id}-book-btn`} className="btn-primary !px-4 !py-3 text-center">
            Book Now
          </Link>
        </div>
      </div>
      <Lightbox images={galleryImages} index={lightbox} onClose={() => setLightbox(null)}
        onNavigate={(d) => setLightbox((i) => (i + d + galleryImages.length) % galleryImages.length)} />
    </motion.article>
  );
}
